import { motion } from "framer-motion";
import { ArrowUpRight, Globe2 } from "lucide-react";

export const Hero = ({ onBookClick }) => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-36 md:pt-44 pb-20 md:pb-28"
    >
      {/* Decorative orbs */}
      <div
        className="orb orb-sage"
        style={{ width: 520, height: 520, top: -120, left: -160 }}
      />
      <div
        className="orb orb-sand"
        style={{ width: 420, height: 420, bottom: -180, right: -120 }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="mb-10 md:mb-12"
          data-testid="hero-logo"
        >
          <img
            src="/logo.jpg"
            alt="Neurological Communication Partners"
            className="w-28 h-28 md:w-36 md:h-36 rounded-3xl ring-1 ring-black/5 shadow-[0_8px_30px_rgb(0,0,0,0.06)] object-cover"
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-xs md:text-sm uppercase tracking-[0.25em] text-[#5C6661] mb-8 flex items-center gap-3"
          data-testid="hero-eyebrow"
        >
          <span className="w-8 h-px bg-[#739072]" />
          Adult Speech-Language Pathologist · SLP · Neurological Care
        </motion.p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.05] text-[#2D332F]"
              data-testid="hero-title"
            >
              Restoring voice,
              <br />
              memory &amp; <em className="text-[#739072]">connection</em>
              <br />
              after neurological change.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-8 text-lg md:text-xl text-[#5C6661] leading-relaxed max-w-2xl"
              data-testid="hero-subtitle"
            >
              Compassionate, evidence-based therapy for adults living with
              Parkinson&rsquo;s disease, cognitive-communication disorders and
              post-stroke recovery — delivered in person and via secure
              telehealth.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={onBookClick}
                data-testid="hero-cta-consult"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#2D332F] text-[#FDFCF8] text-sm tracking-wide hover:bg-[#739072] transition-all duration-300 group"
              >
                Consult with Kathleen
                <ArrowUpRight
                  size={16}
                  strokeWidth={1.5}
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </button>
              <a
                href="#treatments"
                data-testid="hero-cta-treatments"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[#2D332F]/20 text-[#2D332F] text-sm tracking-wide hover:bg-[#2D332F] hover:text-[#FDFCF8] transition-all duration-300"
              >
                Explore Treatments
              </a>
            </motion.div>
          </div>

          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="lg:col-span-5"
          >
            <div className="bg-white/70 backdrop-blur-xl border border-white/40 rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="flex items-center gap-2 text-[#5C6661] text-xs uppercase tracking-[0.25em] mb-6">
                <Globe2 size={14} strokeWidth={1.5} />
                Dual Licensure
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="font-serif text-3xl text-[#2D332F]">USA</p>
                  <p className="text-sm text-[#5C6661] mt-1">
                    New Hampshire &amp; Vermont <br />
                    ASHA CCC-SLP
                  </p>
                </div>
                <div>
                  <p className="font-serif text-3xl text-[#2D332F]">Ireland</p>
                  <p className="text-sm text-[#5C6661] mt-1">
                    CORU-registered <br />
                    Speech &amp; Language Therapist
                  </p>
                </div>
              </div>
              <div className="divider-line my-6" />
              <p className="text-sm text-[#2D332F] leading-relaxed">
                <span className="font-semibold text-[#739072]">Telehealth.</span>{" "}
                Secure, HIPAA-compliant sessions across both jurisdictions.
              </p>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
};
